if (!customElements.get('arabian-soap-bundle')) {
  customElements.define(
    'arabian-soap-bundle',
    class ArabianSoapBundle extends HTMLElement {
      constructor() {
        super();

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.statusNode = this.querySelector('[data-bundle-status]');
        this.variantMap = new Map();
        this.riyalSymbolMarkup = '';
        this.taxIndicatorMarkup = '';
        this.taxMultiplier = Number(this.dataset.taxMultiplier || 1);

        const variantsNode = this.querySelector('[data-variants-json]');
        if (variantsNode) {
          try {
            const variants = JSON.parse(variantsNode.textContent);
            variants.forEach((variant) => {
              this.variantMap.set(String(variant.id), variant);
            });
          } catch (error) {
            console.error('Unable to parse bundle variants JSON', error);
          }
        }

        const riyalSymbolTemplate = this.querySelector('[data-riyal-symbol-template]');
        if (riyalSymbolTemplate) {
          this.riyalSymbolMarkup = riyalSymbolTemplate.innerHTML.trim();
        }

        const taxIndicatorTemplate = this.querySelector('[data-tax-indicator-template]');
        if (taxIndicatorTemplate) {
          this.taxIndicatorMarkup = taxIndicatorTemplate.innerHTML.trim();
        }
      }

      connectedCallback() {
        this.tiers = Array.from(this.querySelectorAll('[data-tier]'));
        this.tiers.forEach((tier) => {
          const toggleButton = tier.querySelector('[data-tier-toggle]');
          const isDefaultTier = tier.dataset.defaultTier === 'true';

          if (toggleButton) {
            toggleButton.addEventListener('click', () => this.setActiveTier(tier));
            toggleButton.addEventListener('keydown', (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              this.setActiveTier(tier);
            });
          }

          tier.querySelectorAll('[data-tier-variant]').forEach((select) => {
            select.addEventListener('change', () => this.updateTierSummary(tier));
          });

          const submitButton = tier.querySelector('[data-tier-submit]');
          if (submitButton) {
            submitButton.addEventListener('click', () => this.addTierToCart(tier, submitButton));
          }

          this.toggleTierPanel(tier, isDefaultTier);
          this.updateTierSummary(tier);
        });

        if (!this.tiers.some((tier) => tier.classList.contains('is-active')) && this.tiers[0]) {
          this.setActiveTier(this.tiers[0]);
        }
      }

      setActiveTier(activeTier) {
        this.tiers.forEach((tier) => {
          this.toggleTierPanel(tier, tier === activeTier);
        });
      }

      toggleTierPanel(tier, isActive) {
        const panel = tier.querySelector('.arabian-soap-bundle__panel');
        const toggle = tier.querySelector('[data-tier-toggle]');

        tier.classList.toggle('is-active', isActive);
        if (panel) {
          panel.hidden = !isActive;
        }

        if (toggle) {
          toggle.setAttribute('aria-expanded', String(isActive));
        }
      }

      updateTierSummary(tier) {
        const selects = Array.from(tier.querySelectorAll('[data-tier-variant]'));
        const regularTotal = selects.reduce((sum, select) => {
          const variant = this.variantMap.get(select.value);
          return sum + (variant ? Number(variant.price) : 0);
        }, 0);

        const pricingMode = tier.dataset.pricingMode || 'fixed';
        const discountPercentage = Number(tier.dataset.discountPercentage || 0);
        const quantity = Number(tier.dataset.tierQty || selects.length || 1);
        let bundlePrice = Number(tier.dataset.bundlePrice || 0);

        if (pricingMode === 'selection_subtotal') {
          bundlePrice = regularTotal;
        } else if (pricingMode === 'percentage_off') {
          const discountedTotal = regularTotal * Math.max(0, 100 - discountPercentage) / 100;
          bundlePrice = Math.round((discountedTotal + Number.EPSILON) * 100) / 100;
        }

        const savings = Math.max(regularTotal - bundlePrice, 0);
        const perBar = quantity > 0 ? bundlePrice / quantity : bundlePrice;
        const displayRegularTotal = regularTotal * this.taxMultiplier;
        const displayBundlePrice = bundlePrice * this.taxMultiplier;
        const displaySavings = savings * this.taxMultiplier;
        const displayPerBar = perBar * this.taxMultiplier;

        const regularNode = tier.querySelector('[data-tier-regular]');
        const regularDetailNode = tier.querySelector('[data-tier-regular-detail]');
        const saveValueNode = tier.querySelector('[data-tier-save-value]');
        const perBarNode = tier.querySelector('[data-tier-per-bar]');
        const bundleTotalNode = tier.querySelector('[data-tier-bundle-total]');

        if (regularNode) {
          regularNode.innerHTML = this.formatMoneyMarkup(displayRegularTotal);
        }

        if (regularDetailNode) {
          regularDetailNode.innerHTML = this.formatMoneyMarkup(displayRegularTotal);
        }

        if (saveValueNode) {
          saveValueNode.innerHTML = this.formatMoneyMarkup(displaySavings);
        }

        if (perBarNode) {
          perBarNode.innerHTML = this.formatMoneyMarkup(displayPerBar);
        }

        if (bundleTotalNode) {
          bundleTotalNode.innerHTML = this.formatMoneyMarkup(displayBundlePrice, { includeTaxIndicator: true });
        }
      }

      formatMoneyMarkup(amount, { includeTaxIndicator = false } = {}) {
        const locale = this.dataset.locale || 'en-SA';
        const safeAmount = Math.round((Number(amount) + Number.EPSILON) * 100) / 100;
        const formatted = new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(safeAmount);
        const currencyMarkup = this.riyalSymbolMarkup
          ? `<span class="sar-price__symbol" aria-hidden="true">${this.riyalSymbolMarkup}</span>`
          : '<span class="sar-price__code">SAR</span>';
        const taxMarkup = includeTaxIndicator && this.taxIndicatorMarkup ? this.taxIndicatorMarkup : '';

        return `
          <span class="sar-price">
            ${currencyMarkup}
            <span class="sar-price__amount">${formatted}</span>
          </span>
          ${taxMarkup}
        `.trim();
      }

      setStatus(message, isError = false) {
        if (!this.statusNode) return;
        this.statusNode.textContent = message || '';
        this.statusNode.classList.toggle('is-error', isError);
      }

      async addTierToCart(tier, button) {
        if (!button || button.classList.contains('loading')) return;

        this.setStatus('');
        button.classList.add('loading');
        button.setAttribute('aria-disabled', 'true');

        try {
          const itemMap = new Map();
          tier.querySelectorAll('[data-tier-variant]').forEach((select) => {
            const key = String(select.value);
            itemMap.set(key, (itemMap.get(key) || 0) + 1);
          });

          const payload = {
            items: Array.from(itemMap.entries()).map(([id, quantity]) => ({
              id: Number(id),
              quantity,
            })),
          };

          if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            if (typeof this.cart.setActiveElement === 'function') {
              this.cart.setActiveElement(button);
            }
          }

          const response = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          });

          const data = await response.json();

          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Unable to add bundle to cart.');
          }

          if (this.cart && typeof this.cart.renderContents === 'function' && data.sections) {
            if (!data.key && Array.isArray(data.items) && data.items[0] && data.items[0].key) {
              data.key = data.items[0].key;
            }
            this.cart.renderContents(data);
          } else {
            window.location.href = window.routes.cart_url;
            return;
          }

          this.setStatus(this.dataset.successLabel || 'Bundle added to cart.');
        } catch (error) {
          console.error(error);
          this.setStatus(error.message || 'Unable to add bundle to cart.', true);
        } finally {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        }
      }
    }
  );
}
